<template>
  <div>
    <div v-if="loading" class="loading-ecg">
      <base-spinner></base-spinner>
    </div>
    <div class="dropdown-container">
      <base-dropdown-select
        v-if="moduleInstance && isChangeECG"
        :options="listChannel"
        v-model="nChannel"
        class="dropdown-min-select bounded wider index"
      ></base-dropdown-select>
    </div>
    <div :id="`canvas-conteiner-${trainingId}`" class="canvas-container">
      <canvas class="myCanvas" :id="`myCanvas-${trainingId}`"></canvas>
      <canvas class="canvas-absolute" :id="`canvasEcg-${trainingId}`"></canvas>
      <canvas class="canvas-absolute" :id="`indicator-${trainingId}`" height="18" width="150"></canvas>
    </div>
  </div>
</template>

<script>
import('@/styles/components/chart.less')
import { subscribeMobileECG } from '@/mixins/subscribe-mobile-ecg.js'
import { wasmFilter } from '@/mixins/wasm-filter.js'
import dotnet from '@/assets/dotnet/dotnet.js'
import { useEcgCanvas } from '@/mixins/ecg-canvas.js'
import { useEcgOnlineCanvas } from '@/mixins/ecg-online-canvas.js'

export default {
  name: 'ActiveTrainingMiniatureEcg',
  components: {
    BaseDropdownSelect: () => import('@/base/BaseDropdownSelect.vue'),
    BaseSpinner: () => import('@/base/BaseSpinner.vue')
  },
  mixins: [subscribeMobileECG, wasmFilter, useEcgCanvas, useEcgOnlineCanvas],
  props: {
    trainingId: {
      type: Number,
      required: true
    }
  },
  data: function() {
    return {
      time: 0,
      maxY: [],
      minY: [],
      storageMaxY: [],
      storageMinY: [],
      lastData: [],
      scaleX: '25',
      scaleY: '5',
      localBuffer: [],
      localBufferNotFilter: [],
      moduleInstance: null,
      source: null,
      dest: null,
      pointOfTime: [],
      mappedId: null,
      leadscount: null,
      nChannel: '1',
      generalLeads: null,
      titleSignals: ['I', 'II', 'III', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6'],
      coordinateGraph: [],
      countPoint: 0,
      animationId: null,
      dotnet: null,
      initFilters: null,
      doFilter: null,
      coefX: 2,
      nbOfSamples: null,
      loading: true,
      drawLoading: false,
      indexFilter: null,
      visibilityHandler: null,
      forceRender: false,
      needsResize: false
    }
  },
  watch: {
    nChannel() {
      this.countPoint = 0
      this.leadscount = this.nChannel === this.generalLeads + 1 ? this.generalLeads : 1
      if (!this.manageWasmBuffers('init')) return
      this.localBufferNotFilter = []
      this.time = 0
      this.ctxEcg.clearRect(0, 0, this.canvasEcg.width, this.canvasEcg.height)
      this.localBuffer = []
      this.createCanvas()
    }
  },
  computed: {
    listChannel() {
      let opt = {}
      for (let i = 0; i < this.generalLeads; i++) {
        opt[i + 1] = `ECG ${this.titleSignals[i]}`
      }
      return opt
    },
    isChangeECG() {
      return this.generalLeads && this.generalLeads > 1
    }
  },
  mounted() {
    let promiseWasmFilter = new Promise(resolve => {
      this.dotnet = dotnet
      if (this.dotnet.getBootStatus() === 'Standby') {
        this.dotnet.boot()
      }
      resolve(this.dotnet)
    })

    promiseWasmFilter
      .then(_dotnet => {
        if (!_dotnet || !_dotnet.WasmFilter) {
          throw new Error('WASM module initialization failed')
        }
        let globalThis = require('globalthis')() // returns native globalThis if compliant
        this.moduleInstance = globalThis.Module
        this.initFilters = _dotnet.WasmFilter.initFilters
        this.doFilter = _dotnet.WasmFilter.doFilters
      })
      .then(() => {
        this.canSubscribe = true
      })

    let self = this
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        if (self.localBuffer[0] && self.localBuffer[0].length >= 7) {
          for (let i = 0; i < self.localBuffer.length; i++) {
            self.localBuffer[i].splice(0, self.localBuffer[i].length - 4)
          }

          self.time = 0
          self.ctxEcg.clearRect(0, 0, self.canvasEcg.width, self.canvasEcg.height)
        }
      }
    }

    document.addEventListener('visibilitychange', this.visibilityHandler)
  },
  beforeDestroy() {
    try {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    } catch (e) {
      console.warn('Error removing visibility listener:', e)
    }

    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }

    this.manageWasmBuffers('free')
  },
  methods: {
    createCanvas() {
      if (!this.leadscount || this.minY.length < this.leadscount || this.maxY.length < this.leadscount) {
        console.warn('Canvas creation delayed: missing channel data')
        return
      }

      const width = this.$el.clientWidth
      const height = 157

      const mainCanvas = this.initCanvas(`myCanvas-${this.trainingId}`, width, height)
      const ecgCanvas = this.initCanvas(`canvasEcg-${this.trainingId}`, width, height)

      if (!mainCanvas || !ecgCanvas) return

      this.canvas = mainCanvas.canvas
      this.ctx = mainCanvas.ctx
      this.canvasEcg = ecgCanvas.canvas
      this.ctxEcg = ecgCanvas.ctx

      this.loading = false
      this.drawGrid(this.ctx, width, height, this.scaleX, this.scaleY, this.pointOfTime)
    },
    createIndicatorLoadingView(width, height) {
      this.indicatorLoadingCtx.globalAlpha = 0.5
      this.indicatorLoadingCtx.fillStyle = 'white'
      this.indicatorLoadingCtx.beginPath()
      this.indicatorLoadingCtx.moveTo(0, 0)
      this.indicatorLoadingCtx.lineTo(width, 0)
      this.indicatorLoadingCtx.lineTo(width, height)
      this.indicatorLoadingCtx.lineTo(0, height)
      this.indicatorLoadingCtx.fill()
      this.indicatorLoadingCtx.globalAlpha = 1
      this.indicatorLoadingCtx.font = 'normal 18px Source Sans Pro, Arial, sans-serif'
      this.indicatorLoadingCtx.fillStyle = '#000'
      this.indicatorLoadingCtx.fillText(
        `${this.$t('src/base/basespinner.9012')}`,
        this.indicatorLoadingCanvas.width / 2,
        this.indicatorLoadingCanvas.height / 2
      )
    },
    changeDataByECG(signal) {
      this.changeData(signal, {
        fixedLeadscount: true,
        fixedArraySize: true,
        logErrors: true
      })
    }
  }
}
</script>
